import {
  FieldArrayPath,
  FieldArrayWithId,
  FieldValues, Path,
  useFieldArray,
  UseFieldArrayProps,
  UseFieldArrayReturn, useFormContext,
} from 'react-hook-form'
import { useMemo } from 'react'

type ArrayItemWithId<
  TForm extends FieldValues,
  TName extends FieldArrayPath<TForm>,
  TItem,
  TKeyName extends string = 'id',
> = FieldArrayWithId<TForm, TName, TKeyName> & TItem & {
  sequenceNumber: number
  deleted: boolean
}

type UseSoftDeleteSequencedFieldArrayReturn<
  TForm extends FieldValues,
  TName extends FieldArrayPath<TForm>,
  TItem,
  TKeyName extends string = 'id',
> = Omit<UseFieldArrayReturn<TForm, TName, TKeyName>, 'fields' | 'append'> & {
  fields: ArrayItemWithId<TForm, TName, TItem, TKeyName>[]
  visibleFields: {
    field: ArrayItemWithId<TForm, TName, TItem, TKeyName>
    index: number
  }[]
  softRemove: (index: number) => void
  moveVisible: (fromVisible: number, toVisible: number) => void
  appendWithSequence: UseFieldArrayReturn<TForm, TName, TKeyName>['append']
}

/**
 * Field-array hook for arrays whose items can be soft-deleted (marked
 * `deleted: true` in-array rather than removed). Visible rows are filtered
 * by `!field.deleted`; the deleted items stay in the form values so the
 * save pipeline can detect them and dispatch the appropriate DELETE.
 *
 * Why in-array and not `fieldArray.remove`:
 *
 * Removing shrinks the array length. With the form-default
 * `shouldUnregister: false`, the now-dead trailing index paths stay in
 * `_names.mount` and RHF's dirty diff (between `_defaultValues` and
 * `_formValues`) flags them as dirty even though no user input touched
 * them. The post-save `form.reset(server, { keepDirtyValues: true })`
 * then "preserves" the user's dirty paths at the dead index — producing
 * a ghost row in the UI (T1-3428 `No.NaN`).
 *
 * In-array `deleted: true` sidesteps that problem: array length never
 * changes, no dead trailing index, no false-dirty flags. The hidden
 * "ghost" row carries `deleted: true` and is filtered by `visibleFields`.
 *
 * Why we also clear `_formState.dirtyFields[name]` on every structural
 * mutation (softRemove / append / moveVisible):
 *
 * `keepDirtyValues: true` preserves dirty paths into the reset payload by
 * path string — index-based for arrays. When the server reshapes the
 * array (delete shrinks length, create assigns id, server may pack),
 * surviving items' indices shift but dirty path strings do not. The
 * preserve loop then writes stale values at indices that now refer to
 * different items, producing hidden ghosts with `deleted: true` landing
 * on freshly created rows, new rows at the wrong index missing
 * `id` / `sequenceNumber` / `type` / `files`, and subsequent saves that
 * try to DELETE the freshly created item.
 *
 * Clearing dirty for the array on every structural mutation eliminates
 * that mismatch — after the next save+reset, the array goes to server
 * state cleanly. Trade-off: on a partial-failure 400 of a newly created
 * row in this array (only after a structural mutation since the last
 * save), the user's typed values for that row are lost and they retype.
 * Pure-edit flows (no structural mutation since last save) still get
 * `keepDirtyValues: true` protection at the correct indices.
 *
 * The full general fix is a snapshot+overlay refactor that matches by
 * item identity rather than index — out of scope here.
 *
 * `_formState` is touched directly because RHF 7 has no public "clear
 * dirty at a path" API. Pinned to react-hook-form@7.44.3.
 */
function useSoftDeleteFieldArray<
  TForm extends FieldValues,
  TItem = Record<string, never>,
  TName extends FieldArrayPath<TForm> = FieldArrayPath<TForm>,
  TKeyName extends string = 'id',
>(
  props: UseFieldArrayProps<TForm, TName, TKeyName>,
): UseSoftDeleteSequencedFieldArrayReturn<TForm, TName, TItem, TKeyName> {
  const fieldArray = useFieldArray(props)
  const { control, getValues } = useFormContext<TForm>()
  const { name } = props

  const clearArrayDirty = () => {
    /* eslint-disable no-underscore-dangle, react-hook-form/no-access-control */
    const dirty = control._formState.dirtyFields as Record<string, unknown> | undefined
    if (dirty == null) return
    delete dirty[name]
    /* eslint-enable no-underscore-dangle, react-hook-form/no-access-control */
  }

  const fields = fieldArray.fields as ArrayItemWithId<TForm, TName, TItem, TKeyName>[]

  const visibleFields = useMemo(
    () => fields
      .map((field, index) => ({ field, index }))
      .filter(({ field }) => !field.deleted),
    [fields],
  )

  const softRemove = (index: number) => {
    const currentFields = getValues(name as Path<TForm>) as ArrayItemWithId<TForm, TName, TItem, TKeyName>[]
    if (!currentFields[index]) return

    const next = currentFields.map((item, i) => (i === index
      ? { ...item, deleted: true }
      : item))

    let seq = 0
    const resequenced = next.map((item) => {
      if (item.deleted) return item

      return {
        ...item,
        // eslint-disable-next-line no-plusplus
        sequenceNumber: seq++,
      }
    })

    fieldArray.replace(resequenced as never)
    clearArrayDirty()
  }

  const appendWithSequence: UseFieldArrayReturn<
  TForm,
  TName,
  TKeyName
  >['append'] = (value) => {
    const currentFields = getValues(name as Path<TForm>) as ArrayItemWithId<TForm, TName, TItem, TKeyName>[]

    const itemsArray = (Array.isArray(value) ? value : [value]) as TItem[]
    const appended = itemsArray.map((item) => ({
      ...item,
      deleted: false,
    }))

    let seq = 0
    const resequenced = [...currentFields, ...appended].map((item) => (item.deleted
      ? item
      // eslint-disable-next-line no-plusplus
      : { ...item, sequenceNumber: seq++ }))

    fieldArray.replace(resequenced as never)
    clearArrayDirty()
  }

  const moveVisible = (fromVisible: number, toVisible: number) => {
    const currentFields = getValues(
      name as Path<TForm>,
    ) as ArrayItemWithId<TForm, TName, TItem, TKeyName>[]

    const visibleIndexes = currentFields
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => !item.deleted)
      .map(({ index }) => index)

    const from = visibleIndexes[fromVisible]
    const to = visibleIndexes[toVisible]

    if (from == null || to == null) return

    const next = [...currentFields]
    const [moved] = next.splice(from, 1)
    if (!moved) return

    next.splice(to, 0, moved)

    let seq = 0
    const resequenced = next.map((item) => {
      if (item.deleted) return item

      return {
        ...item,
        // eslint-disable-next-line no-plusplus
        sequenceNumber: seq++,
      }
    })

    fieldArray.replace(resequenced as never)
    clearArrayDirty()
  }

  return {
    ...fieldArray,
    softRemove,
    visibleFields,
    appendWithSequence,
    moveVisible,
    move: fieldArray.move,
    fields,
  }
}

export default useSoftDeleteFieldArray
